import React, { useEffect, useState } from 'react';
import { getEmployees } from '../services/employeeService';
import {
  getReportRecipients, addReportRecipient, removeReportRecipient, sendReportPreview,
} from '../services/scheduleService';
import useUIStore from '../store/uiStore';

// Weekly report recipients (approved layout 2026-10-25): who gets the
// Monday 9 AM management email. Saves on every add/remove; only
// CEO/System_Admin can edit (also enforced server-side).
const ReportRecipientsCard = () => {
  const showToast = useUIStore((s) => s.showToast);
  const [recipients, setRecipients] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getReportRecipients()
      .then((res) => { setRecipients(res.data.recipients); setCanEdit(res.data.can_edit); })
      .catch(() => showToast('Failed to load report recipients', 'error'));
    getEmployees().then((res) => setEmployees(res.data.employees || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (promise, fallback) => {
    setBusy(true);
    try {
      const res = await promise;
      if (res.data.recipients) setRecipients(res.data.recipients);
      showToast(res.data.message, 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || fallback, 'error');
    } finally {
      setBusy(false);
    }
  };

  const listed = new Set(recipients.map((r) => r.employee_id));
  const addable = employees.filter((e) => e.email && !listed.has(e.id));

  return (
    <div className="glass-card p-6 mb-6">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <h2 className="font-semibold text-gray-900">Weekly report recipients</h2>
        <p className="text-xs text-gray-500">Monday 9 AM management email</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {recipients.length === 0 && <span className="text-sm text-amber-700">Nobody yet - the Monday email won't be sent.</span>}
        {recipients.map((r) => (
          <span key={r.employee_id} className="inline-flex items-center gap-1 text-sm px-3 py-1 rounded-full bg-gray-100">
            {r.name}
            {canEdit && (
              <button aria-label={`Remove ${r.name}`} disabled={busy} className="text-gray-400 hover:text-red-600"
                onClick={() => run(removeReportRecipient(r.employee_id), 'Failed to remove')}>×</button>
            )}
          </span>
        ))}
      </div>
      {canEdit && (
        <>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <select className="border border-gray-300 rounded px-2 py-1.5 text-sm max-w-xs" value="" disabled={busy}
              onChange={(e) => e.target.value && run(addReportRecipient(Number(e.target.value)), 'Failed to add')}>
              <option value="">+ Add a person</option>
              {addable.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <span className="text-xs text-gray-500">Saves immediately. Only people with an email address can be added.</span>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <button disabled={busy} onClick={() => run(sendReportPreview(), 'Failed to send the preview')}
              className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-60">
              Send me a preview now
            </button>
            <span className="text-xs text-gray-500">Emails this week's report to you only</span>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportRecipientsCard;
